import { doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp, deleteDoc, increment, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { createDemoApp } from '../../services/demoAppService';
import { handleFirebaseError, createAuthError } from '../../lib/errors';
import { generateRandomAvatarUrl } from '../../lib/avatarUtils';
import { AuthErrorCode } from '../../types/auth';
import type { User, UserRole, FirestoreUser, AuthResult } from '../../types/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import type { PlanType } from '../../lib/plans';

const isBrowser = typeof window !== 'undefined';

export const userService = {
  /**
   * Create user document in Firestore
   */
  async createUser(
    firebaseUser: FirebaseUser,
    role: UserRole = 'pending',
    overrides?: Partial<User>,
    retries = 3,
    delayMs = 2000
  ): Promise<User> {
    console.log('[userService] Creating user:', { uid: firebaseUser.uid, email: firebaseUser.email, role, retries });
    
    if (!isBrowser) {
      throw new Error('Cannot create user during SSR');
    }

    const isGoogleUser = firebaseUser.providerData.some(p => p.providerId === 'google.com');
    console.log('[userService] Is Google user:', isGoogleUser);

    // Generate default avatar URL using predefined styles
    const defaultAvatarUrl = generateRandomAvatarUrl(firebaseUser.email || firebaseUser.uid);

    // Надёжное получение displayName с явной проверкой на пустоту
    // Приоритет: overrides.displayName -> firebaseUser.displayName -> email -> 'Користувач'
    // Если overrides это строка (для обратной совместимости), используем её как displayName
    let displayName: string;
    if (overrides && typeof overrides === 'object' && 'displayName' in overrides && overrides.displayName) {
      displayName = overrides.displayName;
    } else if (typeof overrides === 'string') {
      displayName = overrides;
    } else if (firebaseUser.displayName && firebaseUser.displayName.trim().length > 0) {
      displayName = firebaseUser.displayName.trim();
    } else if (firebaseUser.email) {
      const emailName = firebaseUser.email.split('@')[0];
      displayName = emailName.trim().length > 0 ? emailName.trim() : 'Користувач';
    } else {
      displayName = 'Користувач';
    }
    
    console.log('[userService] Final displayName:', displayName);
    
    const userData: FirestoreUser = {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName,
      role,
      emailVerified: isGoogleUser || firebaseUser.emailVerified,
      createdAt: serverTimestamp() as Timestamp,
      settings: {
        theme: 'dark'
      },
      avatarUrl: defaultAvatarUrl,
      projectCount: 0,
      demoProjectCount: 0
    };

    console.log('[userService] Creating Firestore doc with:', userData);
    
    // Retry logic with exponential backoff
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        // Wait for Firebase Auth token to sync before retry attempts (not before first)
        if (attempt > 0) {
          const waitTime = delayMs * Math.pow(2, attempt - 1); // Exponential backoff
          console.log(`[userService] Waiting ${waitTime}ms before retry ${attempt + 1}...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
        
        await setDoc(doc(db, 'users', firebaseUser.uid), userData, { merge: true });
        console.log('[userService] User created in Firestore');
        
        return {
          id: firebaseUser.uid,
          email: userData.email,
          displayName: userData.displayName,
          role,
          emailVerified: userData.emailVerified,
          createdAt: new Date().toISOString(),
          settings: userData.settings,
          avatarUrl: userData.avatarUrl
        };
      } catch (error) {
        lastError = error as Error;
        console.error(`[userService] Error creating user (attempt ${attempt + 1}/${retries}):`, error);
        
        // Если это не permission-denied, не retry
        if ((error as any)?.code !== 'permission-denied') {
          throw error;
        }
      }
    }
    
    // All retries failed
    console.error('[userService] All retries failed:', lastError);
    throw lastError;
  },

  /**
   * Get user from Firestore
   */
  async getUser(userId: string): Promise<User | null> {
    if (!isBrowser) return null;

    try {
      console.log('[userService] Getting user from Firestore:', userId);
      const userDoc = await getDoc(doc(db, 'users', userId));

      if (!userDoc.exists()) {
        console.log('[userService] User not found in Firestore:', userId);
        return null;
      }

      const userData = this.convertToUser(userDoc.data() as FirestoreUser, userId);
      console.log('[userService] User found:', userData);
      return userData;
    } catch (error: any) {
      // Логируем более подробную информацию об ошибке
      console.error('[userService] Error getting user:', userId, {
        error,
        code: error?.code,
        message: error?.message,
        toString: error?.toString()
      });
      
      // Если это ошибка доступа - возвращаем null, чтобы не блокировать UI
      if (error?.code === 'permission-denied') {
        console.warn('[userService] Permission denied for user:', userId, '- это нормально, если пользователь не является участником проекта');
        return null;
      }
      
      return null;
    }
  },

  /**
   * Update user data
   */
  async updateUser(userId: string, data: Partial<Omit<User, 'id'>>): Promise<AuthResult<User>> {
    console.log('[userService] updateUser called:', { userId, data });
    
    if (!isBrowser) {
      console.log('[userService] SSR not supported');
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      const updateData: Record<string, unknown> = {};

      if (data.email !== undefined) updateData.email = data.email;
      if (data.displayName !== undefined) updateData.displayName = data.displayName;
      if (data.role !== undefined) updateData.role = data.role;
      if (data.emailVerified !== undefined) updateData.emailVerified = data.emailVerified;
      if (data.settings !== undefined) updateData.settings = data.settings;

      console.log('[userService] Updating Firestore doc with:', updateData);
      await updateDoc(doc(db, 'users', userId), updateData);
      console.log('[userService] Firestore update successful');

      const user = await this.getUser(userId);
      if (!user) {
        console.error('[userService] User not found after update:', userId);
        return {
          success: false,
          error: createAuthError(AuthErrorCode.USER_NOT_FOUND, 'Користувача не знайдено')
        };
      }

      console.log('[userService] User updated successfully:', user);
      return { success: true, data: user };
    } catch (error) {
      console.error('[userService] Error updating user:', error);
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Delete user document from Firestore
   */
  async deleteUser(userId: string): Promise<AuthResult<void>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      await deleteDoc(doc(db, 'users', userId));
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Check if user exists
   */
  async checkUserExists(userId: string): Promise<boolean> {
    if (!isBrowser) return false;

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      return userDoc.exists();
    } catch (error) {
      return false;
    }
  },

  /**
   * Update user role
   */
  async updateRole(userId: string, role: Exclude<UserRole, 'pending'>): Promise<AuthResult<User>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      await updateDoc(doc(db, 'users', userId), { role });

      const user = await this.getUser(userId);
      if (!user) {
        return {
          success: false,
          error: createAuthError(AuthErrorCode.USER_NOT_FOUND, 'Користувача не знайдено')
        };
      }

      return { success: true, data: user };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Create demo project after role selection
   * Uses frontend createDemoApp for full demo data (versions, screens, events)
   */
  async createDemoProject(role: Exclude<UserRole, 'pending'>): Promise<AuthResult<{ appId: string }>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      // Get current user from auth
      const { auth } = await import('../../lib/firebase');
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        return {
          success: false,
          error: createAuthError(AuthErrorCode.USER_NOT_FOUND, 'Користувач не авторизований')
        };
      }

      console.log('[userService] Creating demo project for role:', role, 'userId:', currentUser.uid);
      
      // Use frontend createDemoApp for full demo data with versions
      const appId = await createDemoApp({ userId: currentUser.uid, role });
      
      console.log('[userService] Demo project created:', appId);
      return { success: true, data: { appId } };
    } catch (error) {
      console.error('[userService] Error creating demo project:', error);
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Update theme
   */
  async updateTheme(userId: string, theme: 'light' | 'dark'): Promise<AuthResult<void>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      await updateDoc(doc(db, 'users', userId), { 'settings.theme': theme });
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Update user avatar URL
   */
  async updateAvatar(userId: string, avatarUrl: string): Promise<AuthResult<void>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      await updateDoc(doc(db, 'users', userId), {
        avatarUrl,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Update onboarding status
   */
  async updateOnboardingStatus(
    userId: string,
    status: 'completed' | 'skipped'
  ): Promise<AuthResult<void>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      const now = new Date().toISOString();
      const updateData = status === 'completed'
        ? {
            'settings.onboarding.completed': true,
            'settings.onboarding.completedAt': now,
            'settings.onboarding.skipped': false
          }
        : {
            'settings.onboarding.skipped': true,
            'settings.onboarding.skippedAt': now,
            'settings.onboarding.completed': false
          };

      await updateDoc(doc(db, 'users', userId), updateData);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Mark user as deleted (soft delete)
   */
  async markAsDeleted(userId: string): Promise<AuthResult<void>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      await updateDoc(doc(db, 'users', userId), {
        isDeleted: true,
        deletedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Check if user is deleted
   */
  async isUserDeleted(userId: string): Promise<boolean> {
    if (!isBrowser) return false;

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      return userDoc.exists() && userDoc.data().isDeleted === true;
    } catch (error) {
      return false;
    }
  },

  /**
   * Restore a soft-deleted user
   */
  async restoreUser(userId: string): Promise<AuthResult<void>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      console.log('[userService] Restoring user:', userId);
      await updateDoc(doc(db, 'users', userId), {
        isDeleted: false,
        deletedAt: null,
        restoredAt: serverTimestamp()
      });
      console.log('[userService] User restored successfully:', userId);
      return { success: true };
    } catch (error) {
      console.error('[userService] Error restoring user:', error);
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Update user plan
   */
  async updatePlan(userId: string, plan: PlanType): Promise<AuthResult<User>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      console.log('[userService] Updating plan:', { userId, plan });
      await updateDoc(doc(db, 'users', userId), { plan });

      const user = await this.getUser(userId);
      if (!user) {
        return {
          success: false,
          error: createAuthError(AuthErrorCode.USER_NOT_FOUND, 'Користувача не знайдено')
        };
      }

      console.log('[userService] Plan updated successfully:', user);
      return { success: true, data: user };
    } catch (error) {
      console.error('[userService] Error updating plan:', error);
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Convert Firestore document to User
   */
  convertToUser(data: FirestoreUser, id: string): User {
    const createdAt = data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : (data.createdAt as string) || new Date().toISOString();

    return {
      id,
      email: data.email,
      displayName: data.displayName,
      role: data.role,
      emailVerified: data.emailVerified,
      createdAt,
      settings: data.settings,
      avatarUrl: data.avatarUrl,
      plan: data.plan
    };
  },

  /**
   * Convert Firebase User to User with Firestore data
   */
  async convertFirebaseUser(firebaseUser: FirebaseUser): Promise<User> {
    // Try to get user from Firestore with retry logic
    let userData = await this.getUser(firebaseUser.uid);
    
    // If user is not found, wait and try again
    // This is needed for cases when the user just registered
    // and the trigger hasn't created the document yet
    if (!userData) {
      console.log('[userService] User not found, waiting for trigger...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      userData = await this.getUser(firebaseUser.uid);
    }
    
    // If still not found - try to create a new user with role 'pending'
    // This is needed for cases when the trigger didn't work
    if (!userData) {
      console.log('[userService] User not found in Firestore after waiting, creating:', firebaseUser.uid);
      try {
        return await this.createUser(firebaseUser, 'pending');
      } catch (createError) {
        console.error('[userService] Error creating user:', createError);
        // If we can't create user in Firestore, return a minimal user object
        // The user will be able to proceed but might have limited functionality
        const defaultAvatarUrl = generateRandomAvatarUrl(firebaseUser.email || firebaseUser.uid);
        return {
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Користувач',
          role: 'pending' as UserRole,
          emailVerified: firebaseUser.emailVerified,
          createdAt: new Date().toISOString(),
          settings: { theme: 'dark' },
          avatarUrl: defaultAvatarUrl
        };
      }
    }

    return userData;
  }
};
